import { of as observableOf, Observable, forkJoin } from 'rxjs'
import { Component, OnInit, ViewChild } from '@angular/core'
import {
  IPayer,
  IPlan,
  PayerODataService,
  PlanODataService,
  LineOfBusinessODataService,
  ILineOfBusiness
} from '@exchange/domain/.'
import { ActivatedRoute, Router } from '@angular/router'
import { mergeMap } from 'rxjs/operators'
import { AppSelectComponent } from 'common-components'
import { ToastrService } from 'ngx-toastr'
import { UtilService } from '@exchange/app/services'
import { Option } from 'common-components'
import { PagedResult } from 'common-services'

@Component({
  selector: 'app-plans-edit',
  templateUrl: './plans-edit.component.html',
  styleUrls: ['./plans-edit.component.scss'],
  standalone: false
})
export class PlansEditComponent implements OnInit {
  @ViewChild('payerList', { static: true }) payerList: AppSelectComponent
  @ViewChild('linesOfBusinessList', { static: true })
  linesOfBusinessList: AppSelectComponent

  title = 'Edit Plan'
  model: IPlan = {} as IPlan
  payers: IPayer[] = []
  payerOptions: Option[] = []
  linesOfBusiness: ILineOfBusiness[] = []
  lineOfBusinessOptions: Option[] = []
  busy: any = null

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private odata: PlanODataService,
    private payerService: PayerODataService,
    private linesOfBusinessService: LineOfBusinessODataService,
    private toastr: ToastrService,
    private util: UtilService
  ) {}

  initializeAdd() {
    this.title = 'Add Plan'
    const plan = {
      Id: 0,
      Name: '',
      PayerId: null,
      Payer: null,
      LineOfBusinessId: null,
      AccountingPayerId: null,
      AccountingPayerName: null,
      PayerInvoiceTerm: null,
      ClassName: null
    } as IPlan
    const paged = {
      skipped: 0,
      rowCount: 1,
      data: [plan]
    } as PagedResult<IPlan>

    return observableOf(paged)
  }

  initializeEdit() {
    return this.initializeModel()
  }

  selectPayer = (payerOption: Option = { id: 0, text: '', value: '' } as Option) => {
    this.model.PayerId = +payerOption.id
  }

  selectLineOfBusiness = (lobOption: Option = { id: 0, text: '', value: '' } as Option) => {
    this.model.LineOfBusinessId = +lobOption.id
  }

  private initializeModel(): Observable<PagedResult<IPlan>> {
    return this.route.params.pipe(
      mergeMap(params => {
        const planId = +params['id']
        return this.odata.query().filter(`Id eq ${planId}`).exec()
      })
    )
  }

  private initializePayers() {
    return this.payerService
      .query()
      .select('Id,Name')
      .exec()
      .subscribe(x => {
        this.payers = x.data
        this.payerOptions = x.data.map(y => new Option(y.Id, y.Name, y))

        if (this.model && this.model.PayerId) {
          this.payerList.active = [this.payerOptions.find(y => y.id === this.model.PayerId)]
        }
      })
  }

  private initializeLinesOfBusiness() {
    return this.linesOfBusinessService
      .query()
      .orderBy('Value')
      .exec()
      .subscribe(x => {
        this.linesOfBusiness = x.data
        this.lineOfBusinessOptions = x.data.map(y => new Option(y.Id, y.Value, y))

        if (this.model && this.model.LineOfBusinessId) {
          this.linesOfBusinessList.active = [
            this.lineOfBusinessOptions.find(y => y.id === this.model.LineOfBusinessId)
          ]
        }
      })
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      const id = +params['id']
      const fn = id ? this.initializeEdit.bind(this) : this.initializeAdd.bind(this)

      fn().subscribe((response: PagedResult<IPlan>) => {
        this.model = response.data[0]

        this.initializePayers()
        this.initializeLinesOfBusiness()
      })
    })
  }

  onSubmit(): void {
    const errors = []

    if (!this.model.Name) {
      errors.push('Name is required.')
    }
    if (!this.model.PayerId) {
      errors.push('Payer is required.')
    }
    if (!this.model.LineOfBusinessId) {
      errors.push('Line Of Business is required.')
    }

    this.toastr.clear()
    if (errors.length > 0) {
      this.toastr.error(errors.join('<br />'))
      return
    }

    const $submit = this.model.Id
      ? this.odata.put(this.model, this.model.Id).exec()
      : this.odata.post(this.model).exec()

    this.busy = $submit.subscribe(_ => {
      this.showSuccess('Plan was saved successfully.')
    }, this.showError.bind(this))
  }

  showSuccess(successMessage: string): void {
    this.busy = null
    this.backToList(successMessage)
  }

  showError(response): void {
    this.busy = null

    const msg = this.util.getErrorString(response)

    this.toastr.error(msg)
  }

  backToList(successMessage: string = null) {
    this.router.navigate(['/system', 'plans'], {
      queryParams: { successMessage }
    })
  }
}
